import { useCallback } from 'react'
import logger from 'logger'
import { CancelledPromiseError, PaymentTokenError } from 'helpers'
import { track } from 'analytics'
import { usePaymentFlow, useRecurly } from 'modules/payment'


// token getter for PayPal flow
const usePayPalButton = () => {
  const { recurly } = useRecurly()
  const { submit, isProcessing } = usePaymentFlow()

  const submitPayPal = useCallback(() => {
    track('Express checkout click', {
      paymentMethod: 'PAYPAL',
    })

    const tokenGetter: PaymentModule.TokenGetter = async (params) => {
      const { orderData } = params || {}
      let displayName: string

      if (orderData) {
        const { realPurchaseLevel, hasSubscription } = orderData

        if (realPurchaseLevel === 'SUBSCRIPTION_ONLY') {
          displayName = 'Subscription'
        }
        else if (realPurchaseLevel === 'GIFT_SUBSCRIPTION_ONLY') {
          displayName = 'Gift Subscription'
        }
        else if (realPurchaseLevel === 'GIFT_CARD_ONLY') {
          displayName = 'Gift Card'
        }
        else if (realPurchaseLevel === 'ALL' && hasSubscription) {
          displayName = 'Subscription & Ecommerce Order'
        }
        else {
          displayName = 'Ecommerce Order'
        }
      }
      else {
        // if there is no order data, we just update payment method
        displayName = 'Update payment method'
      }

      logger.info('[PayPal] start getting token')

      // fetch token
      const { token, error } = await new Promise<{ token: { type: string, id: string }, error: Error | string | null }>((resolve) => {
        const payPal = recurly.PayPal({
          display: {
            displayName,
          },
        })

        payPal.on('token', (token) => {
          payPal.destroy()
          resolve({ token, error: null })
        })

        payPal.on('error', (error) => {
          payPal.destroy()
          resolve({ token: null, error })
        })

        payPal.on('cancel', () => {
          payPal.destroy()
          resolve({ token: null, error: null })
        })

        payPal.start()
      })

      // if user just closed the PayPal window, just stop the flow
      if (!token && !error) {
        logger.info(`[PayPal] window was closed`)

        throw new CancelledPromiseError()
      }

      if (error) {
        // parse recurly error
        logger.info(`[PayPal] token error`, {
          extra: {
            error,
          },
        })

        throw new PaymentTokenError(error, error.message)
      }

      logger.info(`[PayPal] token fetched`, {
        extra: {
          token,
        },
      })

      return {
        tokenId: token.id,
      }
    }

    return submit({ tokenGetter, paymentMethod: 'PAYPAL' })
  }, [ submit, recurly ])

  return {
    submitPayPal,
    isProcessing,
  }
}


export default usePayPalButton
